// What shipped on 30 Oct 2026, in one run for a Manager: typing the analysis
// profile, everything that now reads it, the re-index notices, the guided
// conversation changes, and two-step login with the new password rules. It
// only outlines and talks: it never clicks Edit, Publish, Re-index, Start,
// Carry on, Create guide, Add reviewer or Set up two-step login, since each
// one saves, sends, rebuilds or invites. Insights is outlined, not opened:
// opening it asks the model for a summary.

import type { Walkthrough } from './types'

export const whatsNew20261030: Walkthrough = {
  slug: 'whats-new-2026-10-30',
  title: 'What’s new, 30 October',
  summary:
    'Walks through what shipped on 30 October: typing the analysis profile, everything that now reads it, the re-index notices, the guided conversation changes and the new sign-in rules. Nothing is saved, sent, re-indexed or invited.',
  added: '2026-10-30',
  steps: [
    {
      page: 'admin-context',
      feature: 'configuration/analysis-profile',
      control: 'Analysis profile',
      say: 'First, the analysis profile on Context. You can now type it yourself, not only through the agent. Pick the job in Context for to see its card.',
    },
    {
      page: 'admin-context',
      feature: 'configuration/analysis-profile',
      control: 'Edit',
      say: 'Edit opens one box for each section, and a box under them called What changed. With no profile yet, the button reads Write it here. A Manager’s Publish goes live at once. Anyone else’s Propose change waits for a Manager to accept it. Nothing is saved while you watch.',
    },
    {
      page: 'files',
      feature: 'files/folders',
      say: 'Each publish also writes a copy to this job’s Files, in Docs, then Analysis profile, as Analysis profile.md. It is there to read and to hand to the agent. Changing the copy changes nothing; the next publish replaces it.',
    },
    {
      page: 'admin-context',
      feature: 'configuration/analysis-profile',
      control: 'Analysis profile',
      say: 'If the job’s data was re-indexed after the profile was published, the card shows an amber warning with the date. It means: check the profile still holds, then edit it or work on it with the agent.',
    },
    {
      page: 'tables',
      feature: 'tables/insights',
      control: 'Insights',
      say: 'Now everything that describes a job’s data reads that profile. On Tables, the Insights summary does. It ends with a line such as “Based on the Wave One analysis profile, version 3.” I have not opened Insights: it asks the model for a summary.',
    },
    {
      page: 'dashboards',
      feature: 'dataviz/gen-ai-cards',
      say: 'Gen AI cards on a dashboard read it too. The card shows the same sentence as a small caption under its text, “Based on the <job> analysis profile, version N.” It stays on that version until someone regenerates the card, and it goes when someone edits the text by hand.',
    },
    {
      page: 'dashboards',
      feature: 'dataviz/insights',
      say: 'So do the dashboard Insights pane, the chat on a card, and Help me write this in the Gen AI card wizard. Each says which version it read. Agent chats already read the profile and still do.',
    },
    {
      page: 'admin-search',
      feature: 'account/notifications',
      control: 'Notifications',
      say: 'Next, re-index notices. When a job’s tables, variables or uploaded data change after its last index build, each Manager gets one notice in the bell per job. It says what changed and when, and has a Re-index button. I have not pressed it. The notice clears itself when a later build covers the changes.',
    },
    {
      page: 'admin-search',
      feature: 'operations/search-entries',
      control: 'Filter by Job',
      say: 'Search Index shows each job’s index. Filter by Job lists what is indexed for that job, and Indexed At says when. A change to the profile does not need a re-index: only tables, variables and uploaded data do.',
    },
    {
      page: 'agent',
      feature: 'agent/guided-conversation',
      say: 'Guided conversations changed in three ways. A blue box at the top of History in the agent’s side panel lets a person carry on a guided conversation, or start one they were invited to. It shows only when they have one. The agent’s messages are also shorter and more professional.',
    },
    {
      page: 'admin-guides',
      feature: 'people/guide-agenda',
      say: 'A guide can now list the jobs the agent may use. On the guide’s Agenda tab, pick them under Jobs. With none picked, the agent uses the jobs each person uses. With some picked, it asks each person which of them they work on. Reviewers, the people a Manager names to read progress and notes, have their own walkthrough: Guide reviewers.',
    },
    {
      page: 'admin-password',
      feature: 'account/two-step-login',
      control: 'Change Password',
      say: 'Last, sign-in. Two-step login and a Manager’s reset have their own walkthrough, Two-step login. The password rules changed too: at least 12 characters, no common words or keyboard patterns, and no more than 3 identical characters in a row.',
    },
  ],
}
